import React, { useState, useRef, useEffect, useCallback } from "react";
import { convertFilesToBase64, base64ToFile } from "../../utils/fileUtils";
import { PrimaryButton, SecondaryButton } from "../Buttons";
import { Download, FileText, UploadCloud } from "lucide-react";
import axiosInstance from "../../services/api/axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    dedupeMediaByUrl,
    getMediaUrl,
    isMarketingMediaItem,
    normalizeProjectMediaList,
} from "../../utils/media";

const isMarketingType = (mediaType) =>
    mediaType === "marketing_media" || mediaType === "marketing";

const isAgreementType = (mediaType) => mediaType === "agreement";

const displayNameFromKey = (key) => {
    if (!key || typeof key !== "string") return null;
    const base = key.includes("/") ? key.split("/").pop() : key;
    const vndIdx = base.indexOf(".vnd.");
    return vndIdx > 0 ? base.slice(0, vndIdx) : base;
};

const getMediaQueryParams = (mediaType) => {
    if (isAgreementType(mediaType)) {
        return { agreement: true };
    }
    if (isMarketingType(mediaType)) {
        // Support both camelCase (OpenAPI) and snake_case (FastAPI) query names.
        return { marketingMedia: true, marketing_media: true };
    }
    return {};
};

const extractUploadedMediaItems = (response) =>
    normalizeProjectMediaList(response);

const AgreementUpload = ({
    initialAgreement,
    onAgreementChange,
   project,
    projectId,
    type = "agreement" // "agreement" or "marketing"
}) => {
    const [selectedFile, setSelectedFile] = useState(null);
    const fileInputRef = useRef(null);
    
    const [uploading, setUploading] = useState(false);
    const [agreements, setAgreements] = useState([]);
    const [fetchingAgreements, setFetchingAgreements] = useState(false);

    const decodeBase64ToBlob = (rawContent, contentType = "application/octet-stream") => {
        if (!rawContent || typeof rawContent !== "string") return null;

        // Support both data URLs and plain base64 payloads.
        let normalized = rawContent.includes(",") ? rawContent.split(",").pop() : rawContent;
        normalized = normalized.trim().replace(/\s/g, "").replace(/-/g, "+").replace(/_/g, "/");

        // Restore missing padding when needed.
        const pad = (4 - (normalized.length % 4)) % 4;
        normalized += "=".repeat(pad);

        const byteCharacters = atob(normalized);
        const byteArrays = [];
        for (let offset = 0; offset < byteCharacters.length; offset += 1024) {
            const slice = byteCharacters.slice(offset, offset + 1024);
            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
        }

        return new Blob(byteArrays, { type: contentType });
    };

    useEffect(() => {
        // keep selected file state in sync if needed
    }, [initialAgreement]);

    const processMediaList = useCallback(
        (items) =>
            (Array.isArray(items) ? items : [])
                .map((fileData, index) => {
                    try {
                        const presignedUrl = getMediaUrl(fileData);
                        let fileUrl = presignedUrl;

                        if (!fileUrl && fileData.content) {
                            const blob = decodeBase64ToBlob(
                                fileData.content,
                                fileData.contentType
                            );
                            if (!blob) return null;
                            fileUrl = URL.createObjectURL(blob);
                        }

                        if (!fileUrl) return null;

                        const fallbackName = isAgreementType(type)
                            ? `Agreement ${index + 1}`
                            : `File ${index + 1}`;
                        const displayName =
                            fileData.name ||
                            displayNameFromKey(fileData.key) ||
                            fallbackName;

                        return {
                            name: displayName,
                            url: fileUrl,
                            mediaId: fileData.id,
                            fileName: fileData.name || displayNameFromKey(fileData.key),
                            type: fileData.contentType,
                            key: fileData.key,
                        };
                    } catch (decodeError) {
                        console.warn(
                            `Skipping ${type} file:`,
                            fileData?.name || fileData?.key || fileData?.id,
                            decodeError
                        );
                        return null;
                    }
                })
                .filter(Boolean),
        [type]
    );

    const fetchFiles = useCallback(async ({ mergeWithExisting = false } = {}) => {
        if (!projectId) return;

        setFetchingAgreements(true);
        try {
            const response = await axiosInstance.get("/projects/media", {
                params: {
                    project_id: Number(projectId),
                    ...getMediaQueryParams(type),
                },
            });

            let rawItems = normalizeProjectMediaList(response);

            // Fallback: some backends ignore marketingMedia flag — filter from broader list.
            if (isMarketingType(type) && rawItems.length === 0) {
                const fallbackResponse = await axiosInstance.get("/projects/media", {
                    params: {
                        project_id: Number(projectId),
                        agreement: true,
                        images: true,
                        marketingMedia: true,
                        marketing_media: true,
                    },
                });
                rawItems = normalizeProjectMediaList(fallbackResponse).filter(
                    isMarketingMediaItem
                );
            }

            const processedFiles = dedupeMediaByUrl(processMediaList(rawItems));

            setAgreements((prev) => {
                if (!mergeWithExisting) return processedFiles;
                return dedupeMediaByUrl([...processedFiles, ...prev]);
            });
        } catch (error) {
            console.error(`Failed to fetch ${type} files:`, error);
        } finally {
            setFetchingAgreements(false);
        }
    }, [projectId, type, processMediaList]);

    useEffect(() => {
        fetchFiles();
    }, [fetchFiles]);

    const handleFileSelect = (event) => {
        event.stopPropagation();
        const file = event.target.files[0];
        setSelectedFile(file);
    };

    const handleFileUpload = async () => {
        if (!selectedFile ||  !projectId) {
            console.warn("No file selected or missing customer/project ID.");
            return;
        }
        setUploading(true);
        try {
            const base64Files = await convertFilesToBase64([selectedFile]);
            const base64String = base64Files[0].base64;
            const fileName = base64Files[0].name;

            if (isAgreementType(type)) {
                const formData = new FormData();
                formData.append("project_id", projectId);
        const customerId =
          project?.customerId ??
          project?.customer?.customerId ??
          project?.customer?.id ??
          project?.primaryCustomerId ??
          "";
        formData.append("customer_id", customerId);
                const fileObject = base64ToFile(base64String, fileName);
                formData.append("media", fileObject);
                
                await axiosInstance.post("/projects/agreement-template", formData);
                onAgreementChange(base64String);
            } else {
                // For marketing media, use the project prop data
                const currentProject = project;
                console.log("currentProject", currentProject);
     
                const formData = new FormData();
                formData.append("project_name", currentProject.projectName || "");
                formData.append("developer", currentProject.developer || "");
                formData.append("customer_id", currentProject.customerId || "");
                formData.append("address_building_no", currentProject.address?.buildingNo || "");
                formData.append("address_street_name1", currentProject.address?.streetName1 || "");
                formData.append("address_street_name2", currentProject.address?.streetName2 || "");
                formData.append("address_city", currentProject.address?.city || "");
                formData.append("address_district", currentProject.address?.district || "");
                formData.append("address_pin_code", currentProject.address?.pinCode || "");
                formData.append("address_state", currentProject.address?.state || "");
                formData.append("address_country", currentProject.address?.country || "");
              
                formData.append("towers", currentProject.towers || 0);
        
             
                formData.append("size_acres", currentProject.sizeAcres || 0);
               
              
                
                // Add the new marketing media file
                const fileObject = base64ToFile(base64String, fileName);
                formData.append("marketing_media", fileObject);
                
                const uploadResponse = await axiosInstance.put("/projects", formData, {
                    params: { projectId }
                });

                const uploadedItems = extractUploadedMediaItems(uploadResponse);
                if (uploadedItems.length > 0) {
                    const newEntries = processMediaList(uploadedItems);
                    setAgreements((prev) =>
                        dedupeMediaByUrl([...newEntries, ...prev])
                    );
                }
            }
            
            setSelectedFile(null);
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
            toast.success(
                `${isAgreementType(type) ? "Agreement" : "Marketing media"} uploaded successfully!`
            );
            await fetchFiles({ mergeWithExisting: true });

        } catch (error) {
            console.error(`Error uploading ${type}:`, error);
            toast.error(`Error uploading ${type}`);
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                        {isAgreementType(type) ? "Agreement " : "Marketing Media"}
                    </h3>
                    {/* <p className="text-sm text-gray-500 mt-1">
                        {type === "agreement" 
                            ? "Upload agreement templates for your project" 
                            : "Upload marketing materials and brochures"
                        }
                    </p> */}
                </div>
                {isAgreementType(type) && (
                    <SecondaryButton
                        icon={<Download className="size-4 mr-2" />}
                        left
                        className="w-48"
                        onClick={() =>
                            (window.location.href = "/Agreement_Template_Instructions.docx")
                        }
                    >
                        Download Template
                    </SecondaryButton>
                )}
            </div>

            {/* File Upload Area */}
            <div className="mb-6">
                <div className="relative">
                    <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileSelect}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                        accept={isAgreementType(type) ? ".doc,.docx,.pdf" : "image/*,.pdf,.doc,.docx"}
                    />
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                        <div className="flex flex-col items-center">
                            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                                <UploadCloud className="w-6 h-6 text-blue-600" />
                            </div>
                            <h4 className="text-lg font-medium text-gray-900 mb-2">
                                Drop files here or click to browse
                            </h4>
                            <p className="text-sm text-gray-500">
                                {isAgreementType(type)
                                    ? "Supports DOC, DOCX, PDF files"
                                    : "Supports images, PDF, DOC, DOCX files"}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Selected File Preview */}
            {selectedFile && (
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <FileText className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="font-medium text-gray-900">{selectedFile.name}</p>
                                <p className="text-sm text-gray-500">
                                    {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                            </div>
                        </div>
                        <PrimaryButton
                            onClick={handleFileUpload}
                            disabled={uploading}
                            className="bg-blue-600 hover:bg-blue-700"
                        >
                            {uploading ? "Uploading..." : "Upload File"}
                        </PrimaryButton>
                    </div>
                </div>
            )}

            {/* Files List */}
            <div>
                <div className="flex items-center justify-between mb-4">
                    <h4 className="text-md font-semibold text-gray-900">
                        {isAgreementType(type) ? "Uploaded Agreements" : "Uploaded Media"}
                    </h4>
                    {fetchingAgreements && (
                        <div className="text-sm text-gray-500">
                            Loading...
                        </div>
                    )}
                </div>

                {fetchingAgreements ? (
                    <div className="text-center py-8">
                        <div className="text-sm text-gray-500">
                            Loading files...
                        </div>
                    </div>
                ) : agreements && agreements.length > 0 ? (
                    <div className="space-y-3">
                        {agreements.map((file, index) => (
                            <div
                                key={index}
                                className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow duration-200"
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                    <div className="flex items-center space-x-4 min-w-0 w-full sm:w-auto">
                                        <div className="flex-shrink-0">
                                            {file.type?.startsWith('image/') ? (
                                                <img
                                                    src={file.url}
                                                    alt={file.name}
                                                    className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                                />
                                            ) : (
                                                <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                                                    <FileText className="w-6 h-6 text-gray-500" />
                                                </div>
                                            )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-900 truncate">
                                                {file.name}
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1 truncate">
                                                {file.type || 'Unknown type'}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-row items-center space-x-2 mt-2 sm:mt-0 sm:ml-4 shrink-0">
                                        <button
                                            onClick={() => {
                                                const link = document.createElement('a');
                                                link.href = file.url;
                                                link.download = file.fileName || file.name;
                                                document.body.appendChild(link);
                                                link.click();
                                                document.body.removeChild(link);
                                            }}
                                            className="inline-flex items-center justify-center p-2 text-gray-600 hover:text-blue-600 transition-colors duration-200 border border-gray-300 rounded-md hover:border-blue-300"
                                        >
                                            <Download size={17.5} />
                                        </button>
                                        {/* <button
                                            onClick={() => handleFileRemove(file.mediaId)}
                                            className="inline-flex items-center justify-center p-2 text-gray-600 hover:text-red-600 transition-colors duration-200 border border-gray-300 rounded-md hover:border-red-300"
                                        >
                                            <X size={17.5} />
                                        </button> */}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-center py-8">
                        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FileText className="w-8 h-8 text-gray-400" />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No {isAgreementType(type) ? "agreements" : "media"} uploaded yet
                        </h3>
                        <p className="text-sm text-gray-500">
                            Upload your first {isAgreementType(type) ? "agreement template" : "marketing media"} to get started
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AgreementUpload;